import { useEffect, useState } from "react";
import { Tag, Loader2, Save, Sparkles, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const eur = (v) => Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 });

function TierRow({ tier, sconto, onSaved }) {
  const [val, setVal] = useState(String(tier.monthly));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(String(tier.monthly)); }, [tier.monthly]);

  const num = parseFloat(String(val).replace(",", "."));
  const valid = !Number.isNaN(num) && num > 0 && num <= 100000;
  const changed = valid && Math.round(num * 100) !== Math.round(tier.monthly * 100);
  const yearly = valid ? Math.round(num * 12 * (1 - sconto / 100)) : tier.yearly;

  const save = async () => {
    if (!changed) return;
    setBusy(true);
    try {
      const r = await api.put(`/pro/tariffe/${tier.key}`, { monthly: num });
      onSaved(r.data.tiers);
      toast.success(`${tier.label}: prezzo aggiornato` + (r.data.stripe_synced ? " e sincronizzato su Stripe" : " (sync Stripe non riuscita, riprova)"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="flex flex-col gap-3 p-5" data-testid={`tariffa-row-${tier.key}`}>
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <span className="font-heading text-lg font-extrabold">{tier.label}</span>
      </div>
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{tier.range}</div>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="mb-1 block text-xs font-semibold text-muted-foreground">Prezzo mensile (€/mese)</label>
          <Input value={val} onChange={(e) => setVal(e.target.value)} inputMode="decimal"
            className={!valid ? "border-destructive" : ""} data-testid={`tariffa-input-${tier.key}`} />
        </div>
        <Button onClick={save} disabled={!changed || busy} data-testid={`tariffa-save-${tier.key}`} className="shrink-0">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          <span className="ml-1.5">Salva</span>
        </Button>
      </div>
      <div className="border-t pt-3 text-sm text-muted-foreground" data-testid={`tariffa-annuale-${tier.key}`}>
        Annuale (−{sconto}%): <b className="text-foreground">{eur(yearly)} €/anno</b> · {eur(Math.round((yearly / 12) * 100) / 100)} €/mese
        {changed && <span className="ml-2 text-xs text-primary">anteprima, salva per applicare</span>}
      </div>
    </Card>
  );
}

export default function TariffarioVestaPro() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/pro/tariffe").then((r) => setData(r.data)).catch(() => setErr(true));
  }, []);

  if (!isAdmin) return <div className="p-8 text-center text-muted-foreground" data-testid="tariffa-no-access">Accesso riservato all'amministratore.</div>;

  const tiers = data?.tiers || [];
  const sconto = data?.sconto_annuale_pct ?? 20;

  return (
    <div className="mx-auto max-w-5xl p-5 sm:p-8" data-testid="tariffario-vesta-pro-page">
      <div className="mb-2 flex items-center gap-3">
        <Tag className="h-8 w-8 text-primary" strokeWidth={1.6} />
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Tariffario Vesta Pro</h1>
      </div>
      <p className="mb-5 max-w-2xl text-sm text-muted-foreground">
        Prezzi dell'abbonamento Versione Pro per fascia di potenza dell'impianto (kWp), IVA inclusa.
        Modifica il prezzo mensile: l'annuale si calcola da solo con lo sconto del {sconto}%.
      </p>
      <div className="mb-6 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs text-muted-foreground" data-testid="tariffa-stripe-nota">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <span>Al salvataggio il nuovo prezzo viene sincronizzato automaticamente su Stripe. I nuovi abbonamenti pagheranno il prezzo aggiornato; quelli già attivi restano al prezzo sottoscritto.</span>
      </div>

      {err ? (
        <p className="py-16 text-center text-muted-foreground" data-testid="tariffa-error">Tariffario non disponibile al momento.</p>
      ) : !data ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="tariffa-tiers">
          {tiers.map((t) => (
            <TierRow key={t.key} tier={t} sconto={sconto} onSaved={(nt) => setData((d) => ({ ...d, tiers: nt }))} />
          ))}
          <Card className="flex flex-col justify-center border-dashed p-5 text-center" data-testid="tariffa-oltre">
            <div className="font-heading text-lg font-extrabold text-muted-foreground">Oltre 1 MW</div>
            <p className="mt-1 text-xs text-muted-foreground">Tariffa dedicata su richiesta: nessun prezzo a listino.</p>
          </Card>
        </div>
      )}
    </div>
  );
}
